import { Fragment } from 'react';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import { TableBreakdownStack } from '@/features/shared/components/tableSystem/TableMetaCells';
import { CLONE_META_DETAIL_GLYPH_SX } from '@/features/shared/components/tableSystem/TableMetaCells';
import { resolveMetricDeltaArrowDirection } from '@/features/shared/utils/metricDeltaArrow';
import { resolveTableMetricAccentColor } from '@/features/shared/utils/tableMetricPresentation';
import { s, typographyTokens } from '@/theme/tokens';
import { Box } from '@/ui';
import {
  ChecklistRtlOutlinedIcon,
  FlakyIcon,
  ReplayOutlinedIcon,
  TimerOutlinedIcon,
  TrendingDownIcon,
  TrendingUpIcon,
} from '@/ui/primitives/icons';
import type { ReactElement } from 'react';

type MetricTone = 'neutral' | 'green' | 'red' | 'orange' | 'brown' | 'blue' | 'purple' | 'teal';
type MetricDirection = 'higherBetter' | 'lowerBetter';
type MetricKey = 'passRate' | 'frequency' | 'avgDuration' | 'flakeScore';

type TableMetricsBreakdownItem = {
  key: MetricKey;
  label: string;
  valueLabel: string;
  deltaValue: number | null;
  deltaLabel?: string | null;
  isMissing?: boolean;
  tone: MetricTone;
  direction: MetricDirection;
};

type TableMetricsBreakdownCellProps = {
  items: Array<TableMetricsBreakdownItem | null>;
};

const isBreakdownItem = (
  item: TableMetricsBreakdownItem | null,
): item is TableMetricsBreakdownItem => item !== null;

const metricDeltaTextSx = {
  color: 'text.secondary',
  fontSize: typographyTokens.captionSmall.fontSize,
  lineHeight: 1.1,
  display: 'inline-flex',
  alignItems: 'center',
  gap: s('xs'),
  verticalAlign: 'top',
} as const;

const renderMetricTrendIcon = (
  deltaLabel: string | null | undefined,
  rawDeltaDirection: MetricDirection,
  rawDeltaValue: number | null,
): ReactElement | null => {
  if (!deltaLabel || rawDeltaValue === null || rawDeltaValue === 0) return null;
  const isImproving = rawDeltaDirection === 'higherBetter' ? rawDeltaValue > 0 : rawDeltaValue < 0;
  const deltaArrowDirection = resolveMetricDeltaArrowDirection(rawDeltaValue);

  return (
    <Box
      component="span"
      sx={{
        fontSize: typographyTokens.captionSmall.fontSize,
        color: isImproving ? 'success.main' : 'error.main',
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        flex: '0 0 auto',
        height: '1em',
        width: '1em',
      }}
    >
      {deltaArrowDirection === 'down' ? (
        <TrendingDownIcon sx={{ fontSize: 'inherit' }} />
      ) : (
        <TrendingUpIcon sx={{ fontSize: 'inherit' }} />
      )}
    </Box>
  );
};

const resolveMetricIcon = (key: MetricKey): ReactElement => {
  if (key === 'passRate') {
    return (
      <ChecklistRtlOutlinedIcon
        sx={[CLONE_META_DETAIL_GLYPH_SX, { transform: 'translateY(1px)' }]}
      />
    );
  }
  if (key === 'frequency') {
    return <ReplayOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />;
  }
  if (key === 'flakeScore') {
    return <FlakyIcon sx={[CLONE_META_DETAIL_GLYPH_SX, { transform: 'translateY(1px)' }]} />;
  }
  return <TimerOutlinedIcon sx={[CLONE_META_DETAIL_GLYPH_SX, { transform: 'translateY(1px)' }]} />;
};

export const TableMetricsBreakdownCell = ({
  items,
}: TableMetricsBreakdownCellProps): ReactElement => {
  return (
    <TableBreakdownStack>
      {items.filter(isBreakdownItem).map((item) => (
        <Fragment key={item.key}>
          <TableBreakdownLine
            icon={resolveMetricIcon(item.key)}
            content={
              <>
                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                  {item.label}:{' '}
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: item.isMissing
                      ? 'text.secondary'
                      : (theme) => resolveTableMetricAccentColor(theme, item.tone),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.valueLabel}
                </Box>
                {item.deltaLabel ? (
                  <Box
                    component="span"
                    sx={[
                      metricDeltaTextSx,
                      {
                        ml: s('sm'),
                        whiteSpace: 'nowrap',
                      },
                    ]}
                  >
                    {item.deltaLabel}
                    {renderMetricTrendIcon(item.deltaLabel, item.direction, item.deltaValue)}
                  </Box>
                ) : null}
              </>
            }
          />
        </Fragment>
      ))}
    </TableBreakdownStack>
  );
};
